import React, { useState, useRef, useCallback } from 'react';
import Header from './Header';

/**
 * TwoColumnLayout - A custom scrollable two-column layout with hideable header
 * 
 * Features:
 * - Two independently scrollable columns
 * - Header hides on scroll down, shows on scroll up (per column)
 * - When header hides, content expands to fill the space
 * - No scroll position glitches
 */

const SCROLL_THRESHOLD = 5;

function TwoColumnLayout() {
    const [headerVisible, setHeaderVisible] = useState(true);
    const [cvVisible, setCVVisible] = useState(false);
    
    // Track scroll per column
    const scrollMapRef = useRef(new Map());

    const handleScroll = useCallback((e) => {
        const target = e.target;
        
        // Only handle scroll if content is actually scrollable
        const isScrollable = target.scrollHeight > target.clientHeight;
        if (!isScrollable) return;
        
        const currentScroll = target.scrollTop;
        const lastScroll = scrollMapRef.current.get(target) || 0;
        const delta = currentScroll - lastScroll;

        if (Math.abs(delta) > SCROLL_THRESHOLD) {
            if (delta > 0 && currentScroll > 50) {
                // Scrolling down - hide header
                setHeaderVisible(false);
            } else if (delta < 0) {
                // Scrolling up - show header
                setHeaderVisible(true);
            }
            scrollMapRef.current.set(target, currentScroll);
        }
    }, []);

    return (
        <div style={styles.wrapper}>
            {/* Use actual Header component - pass headerVisible to control it */}
            <Header 
                onCVClick={() => setCVVisible(!cvVisible)} 
                cvVisible={cvVisible} 
                setCVVisible={setCVVisible} 
                headerVisible={headerVisible}
            />

            {/* Two Column Container */}
            <div style={styles.columnsContainer}>
                {/* Left Column */}
                <div 
                    className="project-side"
                    style={{
                        ...styles.column,
                        ...styles.leftColumn,
                        paddingTop: headerVisible ? 'var(--header-height, 80px)' : 0,
                        transition: 'padding-top 0.3s ease',
                    }}
                    onScroll={handleScroll}
                >
                    <div style={styles.leftColumnContent}>
                        <h2>Left Column</h2>
                        {/* Generate dummy content */}
                        {Array.from({ length: 20 }, (_, i) => (
                            <div key={i} style={styles.card}>
                                <h3>Left Item {i + 1}</h3>
                                <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
                                Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Column */}
                <div 
                    className="project-side"
                    style={{
                        ...styles.column,
                        ...styles.rightColumn,
                        paddingTop: headerVisible ? 'var(--header-height, 80px)' : 0,
                        transition: 'padding-top 0.3s ease',
                    }}
                    onScroll={handleScroll}
                >
                    <div style={styles.rightColumnContent}>
                        <h2>Right Column</h2>
                        {/* Generate dummy content */}
                        {Array.from({ length: 15 }, (_, i) => (
                            <div key={i} style={{ ...styles.card, ...styles.imageCard }}>
                                <div style={styles.imagePlaceholder}>
                                    Image {i + 1}
                                </div>
                                <p>Caption for image {i + 1}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    wrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
    },
    columnsContainer: {
        display: 'flex',
        width: '100%',
        height: '100%',
    },
    column: {
        flex: 1,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        overscrollBehavior: 'none',
    },
    leftColumn: {
        background: '#f5f5f5',
        borderRight: '1px solid #ddd',
    },
    rightColumn: {
        background: '#ffffff',
    },
    columnContent: {
        padding: 0,
    },
    leftColumnContent: {
        padding: 8,
    },
    rightColumnContent: {
        padding: 0,
    },
    card: {
        background: '#fff',
        padding: 0,
        marginBottom: 15,
        borderRadius: 8,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    imageCard: {
        background: '#f9f9f9',
    },
    imagePlaceholder: {
        width: '100%',
        height: 200,
        background: '#ddd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 24,
        color: '#999',
        marginBottom: 10,
    },
};

export default TwoColumnLayout;
